<template>
    <div class="custom-blockui-container">
        <nav v-if="stek?.identity && isStrategyRoute" class="strategy-nav">
            <div class="nav-group-label">Strategy</div>
            <router-link
                v-for="item in strategyNav"
                :key="item.name"
                :to="withThreadRevision({ name: item.name, params: navParams })"
                class="nav-item"
                :class="[
                    { 'nav-item--active': isStrategyNavActive(item) },
                    strategyDiffClasses(item.diffKey),
                ]"
            >
                <span class="nav-item__icon" :class="item.icon" />
                <span>{{ item.label }}</span>
            </router-link>
        </nav>
        <DevNavigation v-else-if="mode === 'dev' && stek?.identity" />
        <!-- Build has no panel at all (ModeGroupSection.noSubmenu): its one
             page is the design backlog, and a menu of one link is noise. -->
        <template v-else-if="isOverviewRoute || mode === 'build'" />
        <c-stekz-tree
            v-else-if="stek?.identity"
            ref="stekztree"
            class="domains-tree"
            :class="{ 'domains-tree--nested': mode !== 'live' }"
            :data-stek-id="stek?.identity"
            :data-selected-domain-id="domain?.identity ?? ''"
            :data-selected-product-id="product?.identity ?? ''"
            :data-selected-process-id="process?.identity ?? ''"
            :data-mode="mode"
        ></c-stekz-tree>
        <div v-if="isMoving" class="moving-overlay-mask"></div>
        <div v-if="isMoving" class="moving-overlay">
            <ProgressSpinner />
        </div>
    </div>
    <div>
        <Menu v-if="subStageMenuItems.length > 0" :model="subStageMenuItems" />
    </div>

    <!-- Delete confirmation -->
    <ConfirmationDialog
        :displayConfirmation="!!entityToDelete"
        :displaySpinner="isLoading"
        :message="`Are you sure you want to delete ${entityToDelete?.entityType} '${entityToDelete?.name}'?`"
        autofocus="close"
        @close-confirmation="entityToDelete = null"
        @success-confirm="deleteEntity"
    />

    <!-- Move confirmation -->
    <ConfirmationDialog
        :displayConfirmation="showMoveConfirmation"
        :displaySpinner="isLoading"
        :title="`Move '${entityMoveInfo?.entity.name}' to '${entityMoveInfo?.target.name}'`"
        :message="`Moving an entity to a different domain will reset it's history. Are you sure you want to proceed?`"
        autofocus="close"
        @close-confirmation="
            () => {
                showMoveConfirmation = false;
                entityMoveInfo = null;
                isMoving = false;
            }
        "
        @success-confirm="moveEntity"
    />
</template>

<script setup lang="ts">
import { whenever } from "@vueuse/core";

import type { MenuItem } from "primevue/menuitem";
import ProgressSpinner from "primevue/progressspinner";
import { useToast } from "primevue/usetoast";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import DevNavigation from "@/components/DevNavigation.vue";
import {
    STRATEGY_NAV_ITEMS,
    STRATEGY_WATCHERS_ITEM,
    isStrategyRoute as checkIsStrategyRoute,
    isOverviewRouteName,
} from "@/composables/mode-navigation";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { useWorktreeManager } from "@/composables/worktree-manager";
import { diffStatusClasses } from "@/lib/diff-status";
import { api } from "@/lib/openapi";
import { withThreadRevision } from "@/lib/thread-revision-link";
import { baseSuccessToastMessage } from "@/primevue/init";
import ConfirmationDialog from "@/shared/ConfirmationDialog.vue";
import { useDomainsManager, useDomainsStore } from "@/stores/domains-store";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import {
    useProcessesManager,
    useProcessesStore,
} from "@/stores/processes-store";
import { useProductsManager, useProductsStore } from "@/stores/products-store";
import { useUserManager } from "@/stores/user-store";
import "@/wc/stekz-tree/index.js";

const route = useRoute();
const router = useRouter();
const toast = useToast();
const { stek } = useStekManager();
const { organization } = useOrganizationManager();
const { domain, product, process } = useCurrentEntityManager();

const { remove: removeDomain } = useDomainsStore();
const { isLoading: isDomainsLoading } = useDomainsManager();
const { remove: removeProduct } = useProductsStore();
const { isLoading: isProductsLoading } = useProductsManager();
const { remove: removeProcess } = useProcessesStore();
const { isLoading: isProcessesLoading } = useProcessesManager();
const { worktree } = useWorktreeManager();
const { watchersEnabled } = useUserManager();

const isMoving = ref(false);

// `diffKey` maps each Strategy link to its field in the worktree's
// `strategy_diff`, so the link can carry the same added/modified/removed badge
// the entity tree uses. The backend only stamps these on a thread branch.
// Base identity (route name, label, icon) comes from STRATEGY_NAV_ITEMS,
// shared with NavRail's hover-preview flyout so the two can't drift apart —
// only the diff/active-prefix wiring below is specific to this persisted list.
type StrategyDiffKey = "mission_vision" | "objectives" | "horizons";
interface StrategyNavItem {
    name: string;
    label: string;
    icon: string;
    diffKey?: StrategyDiffKey;
    activePrefix?: string;
}
const byName = (name: string) =>
    STRATEGY_NAV_ITEMS.find((item) => item.name === name)!;
const strategyNav = computed<StrategyNavItem[]>(() => [
    { ...byName("strategyVision"), diffKey: "mission_vision" },
    { ...byName("strategyObjectives"), diffKey: "objectives" },
    { ...byName("strategyHorizons"), diffKey: "horizons" },
    { ...byName("strategyMarketCompetitors"), activePrefix: "strategyMarket" },
    // Watchers is not thread-editable, so it carries no diff badge.
    ...(watchersEnabled.value ? [STRATEGY_WATCHERS_ITEM] : []),
]);

const isStrategyNavActive = (item: StrategyNavItem) =>
    item.activePrefix
        ? String(route.name).startsWith(item.activePrefix)
        : route.name === item.name;

// Thread diff badge for a Strategy link — empty (no class) when not viewing a
// thread, since the backend leaves `strategy_diff` all-null off a thread branch.
const strategyDiffClasses = (diffKey?: StrategyDiffKey) =>
    diffKey
        ? diffStatusClasses({
              diff_status: worktree.value?.strategy_diff?.[diffKey],
          })
        : "";
const navParams = computed(() => ({
    orgName: organization.value?.name,
    stekName: stek.value?.name,
}));

const mode = computed(() => {
    return route.matched.find((m) => m.meta.mode)?.meta.mode || "business";
});
// Strategy now has its own rail row and its own panel content — mutually
// exclusive with the domain/product/process tree, even though both live
// within the "business" mode.
const isStrategyRoute = computed(() =>
    checkIsStrategyRoute(route.name as string | undefined)
);
// Overview is a bare landing page — no entity tree; it doesn't own a
// "current worktree" the way domain/product/process pages do.
const isOverviewRoute = computed(() =>
    isOverviewRouteName(route.name as string | undefined)
);
const isDevModeDisabled = computed(
    () => stek.value?.dev_mode_enabled === false
);

const isLoading = computed(() => {
    return (
        isDomainsLoading.value ||
        isProductsLoading.value ||
        isProcessesLoading.value ||
        isMoving.value
    );
});

// The mode switcher itself (Design/Run rail + flyout) moved out of NavSideBar
// into NavRail.vue + ApplicationLayout.vue — see NavRail.spec.ts. Live's
// disabled/tooltip state now comes from useLiveModeTooltip, threaded through
// ApplicationLayout.vue's getModeGroups call — see live-mode-tooltip.spec.ts.
const subStageMenuItems = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [];

    const organizationName = organization.value?.name;
    if (!organizationName) {
        return [];
    }

    // Dashboard button removed - navigation is handled by LiveNavigation tree
    return items;
});

watch(
    [mode, isDevModeDisabled],
    ([currentMode, devModeDisabled]) => {
        if (
            currentMode !== "dev" ||
            !devModeDisabled ||
            !organization.value?.name ||
            !stek.value?.name
        ) {
            return;
        }

        toast.add({
            severity: "info",
            summary: "IT Landscape Disabled",
            detail: "Returning to business.",
            life: 3000,
        });

        router.replace({
            name: "stekPage",
            params: {
                orgName: organization.value.name,
                stekName: stek.value.name,
            },
        });
    },
    { immediate: true }
);

const entityToDelete = ref();

const showMoveConfirmation = ref(false);
const entityMoveInfo = ref();

const stekztree = ref();

const onEntityMoved = (entityInfo) => {
    entityMoveInfo.value = entityInfo;

    const isCrossDomainMove =
        entityInfo.entityType === EntityType.PROCESS
            ? entityInfo.entity?.product?.domain?.identity !==
              entityInfo.target?.domain?.identity
            : entityInfo.entityType === EntityType.PRODUCT
              ? entityInfo.entity?.domain?.identity !==
                entityInfo.target?.identity
              : false;

    if (isCrossDomainMove) {
        // If the move is cross-domain, we need to show a confirmation dialog.
        showMoveConfirmation.value = true;
    } else {
        // If the move is not cross-domain, we can directly proceed with the move.
        showMoveConfirmation.value = false;
        moveEntity();
    }
};

onMounted(async () => {
    // Until we have integrated inline entity creation in the tree,
    // make use of creation modals.
    whenever(
        stekztree,
        () => {
            stekztree.value.addEventListener("delete", (e: CustomEvent) => {
                entityToDelete.value = e.detail;
            });
            stekztree.value.addEventListener("move", (e: CustomEvent) => {
                onEntityMoved(e.detail);
            });
            stekztree.value.addEventListener("reorder", (e: CustomEvent) => {
                const { entityType, ids } = e.detail;
                if (entityType === EntityType.DOMAIN) {
                    useDomainsStore().updateOrders(ids);
                } else if (entityType === EntityType.PRODUCT) {
                    useProductsStore().updateOrders(ids);
                } else if (entityType === EntityType.PROCESS) {
                    useProcessesStore().updateOrders(ids);
                }
            });
            stekztree.value.addEventListener(
                "toast-request",
                (e: CustomEvent) => {
                    toast.add({
                        ...baseSuccessToastMessage,
                        severity: e.detail.type,
                        summary: e.detail.message,
                    });
                }
            );
        },
        { immediate: true }
    );
});

const moveEntity = async () => {
    try {
        isMoving.value = true;
        const { entity, target } = entityMoveInfo.value;
        if (entityMoveInfo.value.entityType === EntityType.PROCESS) {
            await api.startProcessProcessMgtMoveProcessStartPost({
                body: {
                    process_id: entity.identity,
                    target_product_id: target.identity,
                },
            });
        } else if (entityMoveInfo.value.entityType === EntityType.PRODUCT) {
            await api.startProductProductMgtMoveProductStartPost({
                body: {
                    product_id: entity.identity,
                    target_domain_id: target.identity,
                },
            });
        } else {
            console.error("Unknown entity type for move operation");
        }

        // Refresh the tree after successful move
        await stekztree.value?.refreshTree?.();
    } finally {
        isMoving.value = false;
        showMoveConfirmation.value = false;
        entityMoveInfo.value = null;
    }
};

const deleteEntity = async () => {
    if (!entityToDelete.value) return;
    if (!organization.value) return;

    const baseParams = {
        orgName: organization.value?.name,
        stekName: stek.value?.name,
    };

    if (entityToDelete.value.entityType === "domain") {
        await removeDomain(entityToDelete.value.identity);
        await router.push(
            withThreadRevision({
                name: "stekPage",
                params: {
                    ...baseParams,
                },
            })
        );
    } else if (entityToDelete.value.entityType === "product") {
        await removeProduct(entityToDelete.value.identity);
        await router.push(
            withThreadRevision({
                name: "domainPage",
                params: {
                    ...baseParams,
                    domainId: domain.value.identity,
                },
            })
        );
    } else if (entityToDelete.value.entityType === "process") {
        await removeProcess(entityToDelete.value.identity);
        await router.push(
            withThreadRevision({
                name: "productPage",
                params: {
                    ...baseParams,
                    productId: product.value.identity,
                },
            })
        );
    } else {
        console.error("Unknown entity type");
    }
    entityToDelete.value = null;
};
</script>

<style scoped lang="scss">
.custom-blockui-container {
    position: relative;
    display: block;
    // #sidebar is a flex column (rail's panel header + this); flex-grow lets
    // it correctly fill whatever space the header leaves, rather than
    // assuming 100% of #sidebar's own height.
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

c-stekz-tree {
    display: block;
    height: auto;
}

.strategy-nav {
    display: flex;
    flex-direction: column;
    padding: 4px 8px 0;
}

.nav-group-label {
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.04em;
    font-weight: 600;
    color: var(--text-color-secondary);
    padding: 14px 8px 4px;
}

.nav-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 8px;
    color: var(--text-color);
    text-decoration: none;
    cursor: pointer;
}
.nav-item:hover {
    background: var(--stekz-primary-background);
}
.nav-item--active {
    background: var(--p-highlight-background);
    color: var(--p-primary-color);
    font-weight: 600;
}
/* Thread diff indicators on a Strategy link, mirroring the <c-stekz-tree>
   entity badges: green = added, amber = modified, strikethrough = removed. */
.nav-item.diff-added {
    color: var(--stekz-diff-added-color, #16a34a);
}
.nav-item.diff-modified {
    color: var(--stekz-diff-modified-color, #d97706);
}
.nav-item.diff-removed {
    text-decoration: line-through;
    opacity: 0.6;
}
.nav-item__icon {
    flex: 0 0 auto;
    font-size: 1rem;
    width: 1.1em;
    text-align: center;
}

.moving-overlay-mask {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 999;
    background: rgba(0, 0, 0, 0.1);
    cursor: not-allowed;
}

.profile-link {
    color: unset;
    text-decoration: inherit;
}

.menu {
    border: none;
    width: fit-content;
}

.moving-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
}

.moving-overlay p {
    margin: 0;
    color: var(--text-color);
    font-weight: 500;
}

.select-organization {
    text-decoration: none;
}
</style>
